// CARQ-72：前端「過期 bundle 自癒」（stale-bundle self-heal）。
//
// 根因（Bridget 5/28 的決定性觀察收斂出來的）：本服務前面有 Cloudflare。index.html 雖已設
// 真正的 no-store HTTP 標頭（commit 5113351 + SPA catch-all），但「在修正前就被瀏覽器／CDN
// 快取下來的舊版 index.html」仍會被服務於『原始網址』（例如 /edit/:id）——它指向舊的內容雜湊
// JS chunk（main.[hash].js），使用者因此實際跑到上一版（含 bug）的程式碼，按鈕看起來「沒反應」。
//
// Bridget 的關鍵回報：在網址後面加任意 query（?carq72debug=1）就正常、拿掉就壞。這正說明
// 「差別只在 query 改變了快取 key → 抓到一份未被快取的新版 index.html」，是純快取問題，
// 與按鈕邏輯無關（Modal 早已用 React Portal、handler 是純 setState）。
//
// 對策：啟動時比對兩個指紋——
//   ① 目前實際在跑的 bundle 指紋：讀 DOM 裡 <script src=".../static/js/main.[hash].js">。
//   ② 線上最新建置指紋：GET /api/version（位於 no-store 的 /api 之下，CDN 不會快取，永遠最新）。
// 兩者不一致 → 跑的是過期 bundle → 自動帶一個 cache-bust query（?_v=<最新指紋>）整頁重載，
// 把 Bridget 手動加 query 的繞法自動化。用 sessionStorage 記錄「已為此指紋重載過」，避免在
// 極端情況（連 cache-bust 後仍拿到舊版）陷入無限重載迴圈。
//
// 重要：本自癒只在「啟動時」跑一次（使用者尚未開始編輯），不在計時器中途重載，
// 以免毀掉 QuotationForm 編輯中的未存表單資料。

// 純函式：從一組 <script> 的 src 取出 main chunk 的內容雜湊（即 bundle 指紋）。
export function loadedMainHashFromScripts(scriptSrcs) {
  if (!Array.isArray(scriptSrcs)) return null;
  for (const src of scriptSrcs) {
    const m = (typeof src === 'string' ? src : '').match(/\/static\/js\/main\.([A-Za-z0-9]+)\.js/);
    if (m) return m[1];
  }
  return null;
}

// 純函式：依「跑的指紋 vs 線上指紋 vs 本次是否已為該指紋重載過」決定要不要重載。
// 抽成純函式以利單元測試，並把所有不該重載的情況都明確列出（避免誤觸發/迴圈）。
export function decideReload({ loadedHash, serverBuild, alreadyTriedForBuild }) {
  if (!serverBuild || serverBuild === 'unknown') return { reload: false, reason: 'server-unknown' };
  if (!loadedHash) return { reload: false, reason: 'loaded-unknown' };
  if (loadedHash === serverBuild) return { reload: false, reason: 'fresh' };
  if (alreadyTriedForBuild === serverBuild) return { reload: false, reason: 'already-tried' };
  return { reload: true, reason: 'stale' };
}

// 純函式：在現有網址加上 cache-bust 參數 ?_v=<指紋>。沿用 query 而非清掉，是刻意的：
// 帶 _v 的網址是一個「永遠新鮮」的快取 key，使用者之後再重載也不會掉回被快取的舊版。
export function buildReloadUrl(href, serverBuild) {
  const u = new URL(href);
  u.searchParams.set('_v', serverBuild);
  return u.toString();
}

const STORAGE_KEY = 'carq72_reloaded_for';

// 執行自癒檢查。所有外部相依（fetch / document / window）皆可注入以利測試；
// 任何錯誤都吞掉並回 { reload:false }，絕不可影響 App 啟動。
export async function runVersionGuard({
  fetchImpl = typeof fetch !== 'undefined' ? fetch : null,
  doc = typeof document !== 'undefined' ? document : null,
  win = typeof window !== 'undefined' ? window : null,
} = {}) {
  if (!fetchImpl || !doc || !win) return { reload: false, reason: 'no-env' };
  try {
    const scriptSrcs = Array.from(doc.querySelectorAll('script[src]')).map((s) => s.src);
    const loadedHash = loadedMainHashFromScripts(scriptSrcs);

    const res = await fetchImpl('/api/version', { cache: 'no-store' });
    if (!res || !res.ok) return { reload: false, reason: 'version-fetch-failed' };
    const info = await res.json();
    const serverBuild = info && info.build;

    let alreadyTriedForBuild = null;
    try {
      alreadyTriedForBuild = win.sessionStorage.getItem(STORAGE_KEY);
    } catch (_) {
      // sessionStorage 不可用（隱私模式等）→ 視為沒重載過
    }

    const decision = decideReload({ loadedHash, serverBuild, alreadyTriedForBuild });
    if (!decision.reload) return { ...decision, loadedHash, serverBuild };

    try {
      win.sessionStorage.setItem(STORAGE_KEY, serverBuild);
    } catch (_) {
      // 無法寫入也照樣重載；最差情況是這個 tab 重載兩次後因 fresh 而停止
    }
    win.location.replace(buildReloadUrl(win.location.href, serverBuild));
    return { reload: true, reason: 'stale', loadedHash, serverBuild };
  } catch (_) {
    return { reload: false, reason: 'error' };
  }
}
